import { FEATURE_FLAG_KEYS } from '@alpha-core/feature-flags/feature-flag.constants';
import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { FeatureFlagsService } from '@neutrinos/ngx-feature-flags';
import { filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class BranchStateService {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly featureFlagsService = inject(FeatureFlagsService);

    private readonly _currentBranch = signal<string>('main');
    readonly currentBranch = this._currentBranch.asReadonly();

    constructor() {
        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            this._currentBranch.set(params['branch'] || 'main');
        });

        this.setupNavigationInterceptor();
    }

    /** Returns branch for API calls. Always returns the branch name (including 'main'). */
    getBranch(): string {
        return this._currentBranch();
    }

    /** Set branch synchronously (used by resolver before config fetches). */
    setBranch(branch: string): void {
        this._currentBranch.set(branch || 'main');
    }

    /** Get current branch from URL query params (static method for use before service init) */
    static getBranchFromUrl(): string {
        const params = new URLSearchParams(window.location.search);
        return params.get('branch') || 'main';
    }

    /** Append branch to URL */
    appendBranchToUrl(url: string): string {
        const branch = this.getBranch();
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}branch=${encodeURIComponent(branch)}`;
    }

    private setupNavigationInterceptor(): void {
        this.router.events
            .pipe(
                filter((event): event is NavigationStart => event instanceof NavigationStart),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((event) => {
                this.handleNavigation(event);
            });
    }

    private handleNavigation(event: NavigationStart): void {
        // Skip branch injection when change tracking is disabled
        if (!this.featureFlagsService.isFeatureEnabled(FEATURE_FLAG_KEYS.CHANGE_TRACKING)) {
            return;
        }

        const targetUrl = this.router.parseUrl(event.url);
        const targetBranch = targetUrl.queryParams['branch'];

        // If target already has branch, nothing to do
        if (targetBranch) {
            return;
        }

        // Get current branch from signal or default to 'main'
        const currentBranch = this._currentBranch() || 'main';

        // Add branch to target URL and redirect (always required)
        targetUrl.queryParams = { ...targetUrl.queryParams, branch: currentBranch };
        this.router.navigateByUrl(targetUrl);
    }
}
